import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { TextArea } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { useMsal } from "@azure/msal-react";

import { APIS } from "src/utils/constants";
import { isFeatureEnabled, featureFlags } from "src/utils/featureFlag";
import { createInstance } from "src/utils/axios";
import { useTokenContext } from "src/contexts/tokenContext";
import * as styles from "./FeedbackButton.css";

/**
 * Dev/staging-only floating "Feedback" button. Renders fixed to the
 * bottom-right of the viewport, above Kendo dialogs (z-index 11000 vs
 * Kendo's 10003), so users can leave feedback even when a modal is
 * open. The submission record captures:
 *
 *  - the route path (`window.location.pathname`)
 *  - the page title (the first `<h1>` text, best-effort)
 *  - the title of any non-feedback `.k-dialog` open at submit time
 *  - category (UI / Data / Bug / Idea)
 *  - free-text comment
 *  - the user's email + oid from MSAL claims
 *  - browser User-Agent
 *  - deploy environment label
 *
 * Gated behind the `Feedback` feature flag (development + staging only).
 */
type Category = "UI" | "DATA" | "BUG" | "IDEA";
const CATEGORY_LABELS: Record<Category, string> = {
  UI: "UI / UX",
  DATA: "Data",
  BUG: "Bug",
  IDEA: "Idea",
};

const FEEDBACK_DIALOG_MARKER = "rp-feedback-dialog";

function captureContext(): {
  screenPath: string;
  screenTitle: string | null;
  modalTitle: string | null;
} {
  const screenPath = window.location.pathname;
  const screenTitle = document.querySelector("h1")?.textContent?.trim() || null;
  // Find any open Kendo dialog that isn't our own.
  const dialogs = Array.from(document.querySelectorAll(".k-dialog"));
  const otherDialog = dialogs.find((d) => !d.classList.contains(FEEDBACK_DIALOG_MARKER));
  const modalTitle =
    otherDialog?.querySelector(".k-dialog-title")?.textContent?.trim() ||
    otherDialog?.querySelector(".k-window-title")?.textContent?.trim() ||
    null;
  return { screenPath, screenTitle, modalTitle };
}

const MAX_COMMENT = 2000;

const FeedbackButton = () => {
  const enabled = isFeatureEnabled(featureFlags.Feedback);
  const { accessToken, setAccessToken } = useTokenContext();
  const { accounts } = useMsal();
  const axiosInstance = useMemo(
    () => createInstance(accessToken, setAccessToken),
    [accessToken, setAccessToken],
  );

  const [open, setOpen] = useState(false);
  const [context, setContext] = useState<ReturnType<typeof captureContext> | null>(null);
  const [category, setCategory] = useState<Category>("BUG");
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!context) throw new Error("No context captured.");
      const env =
        import.meta.env.VITE_NODE_ENV ?? import.meta.env.MODE ?? "development";
      const body = {
        screenPath: context.screenPath,
        screenTitle: context.screenTitle,
        modalTitle: context.modalTitle,
        category,
        commentText: comment.trim(),
        userAgent: navigator.userAgent.slice(0, 500),
        environment: env.slice(0, 20),
      };
      const r = await axiosInstance.post(APIS.UserFeedback, body);
      return r.data;
    },
    onSuccess: () => {
      setSubmitted(true);
      // Auto-close after a beat so the user sees the confirmation.
      setTimeout(() => closeAll(), 1500);
    },
  });

  if (!enabled) return null;

  const handleOpen = () => {
    setContext(captureContext());
    setCategory("BUG");
    setComment("");
    setSubmitted(false);
    mutation.reset();
    setOpen(true);
  };

  const closeAll = () => {
    setOpen(false);
    setContext(null);
  };

  const userEmail = accounts[0]?.username ?? "(unknown)";
  const canSubmit = comment.trim().length >= 4 && !mutation.isPending;

  return (
    <>
      <button
        type="button"
        className={styles.fab}
        onClick={handleOpen}
        aria-label="Leave feedback"
      >
        Feedback
      </button>

      {open && (
        <Dialog
          title="Leave feedback"
          onClose={closeAll}
          className={FEEDBACK_DIALOG_MARKER}
          width={500}
        >
          <div className={styles.dialogBody}>
            <div className={styles.contextStrip}>
              <span className={styles.contextLabel}>About</span>
              <span className={styles.contextValue}>
                {context?.screenTitle ?? context?.screenPath ?? "(unknown screen)"}
                {context?.modalTitle ? ` → ${context.modalTitle}` : ""}
              </span>
              <span className={styles.contextLabel}>From</span>
              <span className={styles.contextValue}>{userEmail}</span>
            </div>

            <div>
              <div className={styles.fieldLabel}>Category</div>
              <div className={styles.categoryRow} role="radiogroup" aria-label="Category">
                {(Object.keys(CATEGORY_LABELS) as Category[]).map((c) => (
                  <button
                    key={c}
                    type="button"
                    role="radio"
                    aria-checked={category === c}
                    className={`${styles.categoryChip} ${
                      category === c ? styles.categoryChipSelected : ""
                    }`}
                    onClick={() => setCategory(c)}
                  >
                    {CATEGORY_LABELS[c]}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className={styles.fieldLabel}>Comment</div>
              <TextArea
                value={comment}
                onChange={(e) =>
                  setComment(String(e.value ?? "").slice(0, MAX_COMMENT))
                }
                rows={5}
                autoSize={false}
                placeholder="What did you notice? Steps to repro are helpful for bugs."
              />
              <div className={styles.charCount}>
                {comment.length} / {MAX_COMMENT}
              </div>
            </div>

            {submitted && (
              <div className={styles.success}>Thanks — your feedback was saved.</div>
            )}
            {mutation.isError && (
              <div className={styles.errorBox}>
                Couldn't save feedback: {(mutation.error as Error)?.message ?? "unknown error"}
              </div>
            )}
          </div>

          <DialogActionsBar>
            <Button onClick={closeAll} disabled={mutation.isPending}>
              {submitted ? "Close" : "Cancel"}
            </Button>
            <Button
              themeColor="primary"
              onClick={() => mutation.mutate()}
              disabled={!canSubmit || submitted}
            >
              {mutation.isPending ? "Sending…" : "Submit"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
};

export default FeedbackButton;
